import axios from "axios";

/*
 * The talent's session in the CBT app.
 *
 * The talent app opens this app with ?code=<one-time code>. At start-up the
 * code is traded at POST /api/v1/auth/exchange for a token pair, the pair is
 * kept in localStorage, and the code is removed from the address bar. So no
 * token ever sits in the URL, the browser history or a server log.
 *
 * When the access token is refused (401), the refresh token gets a new pair
 * from POST /api/v1/auth/refresh, and the request is tried once more.
 *
 * Until every talent app has the new link, an old ?token=<JWT> link still
 * works: the token is kept the same way and also removed from the address bar.
 */

// The same key the app always used, so a session already stored keeps working
export const TOKEN = "droomwork-cbt-token";
export const REFRESH_TOKEN = "droomwork-cbt-refresh";

// A plain client for /auth/*, without the app's interceptors, so a failed
// refresh can never trigger another refresh
const authClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL + "/api/v1",
  timeout: 20000,
});

// localStorage can throw (private mode, blocked storage): treat that as empty
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStorage(key: string, value: string | null) {
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    // nothing to do: the session simply lasts for this page only
  }
}

export function getAccessToken(): string | null {
  return readStorage(TOKEN);
}

export function getRefreshToken(): string | null {
  return readStorage(REFRESH_TOKEN);
}

export function saveSession(accessToken: string, refreshToken: string | null) {
  writeStorage(TOKEN, accessToken);
  writeStorage(REFRESH_TOKEN, refreshToken);
}

export function clearSession() {
  writeStorage(TOKEN, null);
  writeStorage(REFRESH_TOKEN, null);
}

// Remove query parameters from the address bar, without reloading the page
function removeFromAddressBar(names: string[]) {
  const url = new URL(window.location.href);
  names.forEach((name) => url.searchParams.delete(name));
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
}

/*
 * Run once at start-up, before any other request: take a ?code= (or an old
 * ?token=) from the address bar, store the session, and remove it from the URL.
 */
export async function takeSessionFromUrl(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const legacyToken = params.get("token");

  if (code) {
    try {
      const { data } = await authClient.post("/auth/exchange", { code: code });
      const pair = data && data.data;
      if (pair && pair.accessToken) {
        saveSession(pair.accessToken, pair.refreshToken || null);
      }
    } catch (error) {
      // An expired or already used code: keep whatever session was stored.
      // If there is none, the test page shows its "couldn't load" message.
      console.log("Could not exchange the login code");
    }
  } else if (legacyToken && legacyToken.trim() !== "") {
    saveSession(legacyToken.trim(), null);
  }

  if (code || legacyToken) {
    removeFromAddressBar(["code", "token"]);
  }
}

// While a refresh is running, every request that needs one waits for the same one
let refreshInProgress: Promise<string | null> | null = null;

/*
 * Get a new access token with the refresh token.
 * Returns the new access token, or null when the session is over.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (refreshInProgress) {
    return refreshInProgress;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.resolve(null);
  }

  refreshInProgress = authClient
    .post("/auth/refresh", { refreshToken: refreshToken })
    .then(({ data }) => {
      const pair = data && data.data;
      if (!pair || !pair.accessToken) {
        return null;
      }
      saveSession(pair.accessToken, pair.refreshToken || refreshToken);
      return pair.accessToken as string;
    })
    .catch(() => null)
    .finally(() => {
      refreshInProgress = null;
    });

  return refreshInProgress;
}
